/**
 * Meteorological Service (drawer, hotkey W): the theatre's weather now, the 24 h outlook as a
 * small map (regions shaded by the forecast at the chosen lead time) and a regional table —
 * front regions first — plus the latest MET dispatches. Chances are the ensemble's honest ones:
 * a day ahead they spread out, a few hours ahead they are sharp.
 */
import { dayOf, EDGE_CORNERS, type GameMap, type HexGrid, isWater, type PlayerView } from '@friction/engine';
import { useEffect, useMemo, useRef, useState } from 'react';
import { bus } from '../../game/bus';
import { cssColor, describeEvent, formatClock, formatDate } from '../../game/format';
import { useGame } from '../../game/store';
import {
  formatTemp,
  OUTLOOK_ICON,
  type Outlook,
  outlookAt,
  seasonBlurb,
  seasonLabel,
  theatreShares,
  windFrom,
} from '../../game/weather';
import { type Translator, useT } from '../../i18n';
import { regionName } from '../../i18n/names';
import { cx } from '../common/controls';
import { Icon } from '../common/Icon';
import { DrawerHead } from './DrawerHead';
import d from './drawers.module.css';
import hud from './hud.module.css';
import m from './met.module.css';

/** Forecast steps shown (indices into `forecast.leads`: +6 h, +12 h, +18 h, +24 h). */
const STEPS = [1, 3, 5, 7];

const outlookWord = (t: Translator, kind: Outlook['kind']): string => t(`outlook.${kind}`);

/** Map shading per outlook: rgb, drawn with alpha ∝ chance. */
const OUTLOOK_RGB: Record<Outlook['kind'], [number, number, number]> = {
  clear: [0, 0, 0],
  rain: [58, 104, 150],
  storm: [74, 48, 112],
  snow: [236, 242, 248],
  blizzard: [255, 255, 255],
  fog: [150, 146, 136],
};

/** Regions along our front (own hexes next to believed-hostile ones), then where our units stand. */
function regionRanks(
  view: PlayerView,
  map: GameMap,
  grid: HexGrid,
): { front: Set<number>; ours: Set<number> } {
  const front = new Set<number>();
  const ours = new Set<number>();
  const team = view.factions.find((f) => f.id === view.faction)?.team ?? -1;
  const hostile = (o: number) => o < view.factions.length && view.factions[o].team !== team;
  for (let h = 0; h < grid.count; h++) {
    const r = map.region[h];
    if (r < 0 || view.owner[h] !== view.faction) continue;
    ours.add(r);
    if (grid.neighbourList(h).some((n) => hostile(view.owner[n]))) front.add(r);
  }
  return { front, ours };
}

function OutlookMap({ step }: { step: number }) {
  const t = useT();
  const view = useGame((g) => g.view);
  const map = useGame((g) => g.map);
  const grid = useGame((g) => g.grid);
  const ref = useRef<HTMLCanvasElement>(null);
  const [hover, setHover] = useState<string | null>(null);
  const W = 368;
  const H = grid ? Math.round((W * grid.worldHeight) / grid.worldWidth) : 240;

  useEffect(() => {
    const cv = ref.current;
    if (!cv || !view || !map || !grid) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width = W * dpr;
    cv.height = H * dpr;
    const g = cv.getContext('2d');
    if (!g) return;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    const k = W / grid.worldWidth;
    const r = 36 * k * 1.02;
    const f = view.weather.forecast;
    const me = view.factions.find((x) => x.id === view.faction);
    g.fillStyle = '#1b1d20';
    g.fillRect(0, 0, W, H);
    const hexPath = (h: number) => {
      const cx = grid.cx(h) * k;
      const cy = grid.cy(h) * k;
      g.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i - Math.PI / 6;
        g.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
      }
      g.closePath();
    };
    for (let h = 0; h < grid.count; h++) {
      const t = map.terrain[h];
      if (isWater(t)) {
        hexPath(h);
        g.fillStyle = '#2b3a44';
        g.fill();
        continue;
      }
      hexPath(h);
      g.fillStyle =
        view.owner[h] === view.faction && me ? mix(cssColor(me.color), '#d8cfa9', 0.72) : '#cfc6a2';
      g.fill();
      const region = map.region[h];
      if (region < 0) continue;
      const o = outlookAt(f, region, step);
      if (o.kind === 'clear') continue;
      const [cr, cg, cb] = OUTLOOK_RGB[o.kind];
      g.fillStyle = `rgba(${cr},${cg},${cb},${(0.25 + 0.6 * (o.chance / 100)).toFixed(3)})`;
      g.fill();
    }
    // Region borders, thin ink.
    g.strokeStyle = 'rgba(43, 38, 32, 0.55)';
    g.lineWidth = 0.8;
    g.beginPath();
    for (let h = 0; h < grid.count; h++) {
      const reg = map.region[h];
      if (reg < 0) continue;
      for (let dir = 0; dir < 3; dir++) {
        const n = grid.neighbour(h, dir);
        if (n < 0 || map.region[n] === reg || map.region[n] < 0) continue;
        const [a, b] = EDGE_CORNERS[dir];
        const pa = corner(grid, h, a, k);
        const pb = corner(grid, h, b, k);
        g.moveTo(pa[0], pa[1]);
        g.lineTo(pb[0], pb[1]);
      }
    }
    g.stroke();
  }, [view, map, grid, step, H]);

  if (!view || !map || !grid) return null;
  const regionAt = (e: React.MouseEvent<HTMLCanvasElement>): number => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * grid.worldWidth;
    const y = ((e.clientY - rect.top) / rect.height) * grid.worldHeight;
    const h = grid.fromWorld(x, y);
    return h >= 0 ? map.region[h] : -1;
  };
  return (
    <div className={m.mapWrap}>
      <canvas
        ref={ref}
        className={m.map}
        style={{ width: W, height: H }}
        onMouseMove={(e) => {
          const r = regionAt(e);
          if (r < 0) return setHover(null);
          const o = outlookAt(view.weather.forecast, r, step);
          const word = outlookWord(t, o.kind);
          setHover(
            t('met.hover', {
              region: regionName(map.regions[r].name, t.locale),
              word: t.locale === 'en' ? word.toLowerCase() : word,
              chance: o.chance,
            }),
          );
        }}
        onMouseLeave={() => setHover(null)}
        onClick={(e) => {
          const r = regionAt(e);
          if (r >= 0) bus.emit('focusHex', { hex: map.regions[r].hex });
        }}
      />
      <div className={m.mapHover}>{hover ?? t('met.clickRegion')}</div>
    </div>
  );
}

/** World-space corner i of hex h (pointy-top, angle 60°·i − 30°), scaled. */
function corner(grid: HexGrid, h: number, i: number, k: number): [number, number] {
  const a = (Math.PI / 3) * i - Math.PI / 6;
  return [(grid.cx(h) + 36 * Math.cos(a)) * k, (grid.cy(h) + 36 * Math.sin(a)) * k];
}

function mix(a: string, b: string, t: number): string {
  const p = (s: string, i: number) => Number.parseInt(s.slice(1 + i * 2, 3 + i * 2), 16);
  const c = [0, 1, 2].map((i) => Math.round(p(a, i) * (1 - t) + p(b, i) * t));
  return `rgb(${c[0]},${c[1]},${c[2]})`;
}

function Cell({ o }: { o: Outlook }) {
  const t = useT();
  return (
    <span
      className={cx(m.cell, m[`o_${o.kind}`])}
      title={t('met.cell', { word: outlookWord(t, o.kind), chance: o.chance })}
    >
      <Icon name={OUTLOOK_ICON[o.kind]} size={14} />
      <span className={m.chance}>{o.kind === 'clear' ? '' : o.chance}</span>
    </span>
  );
}

export function MetDrawer() {
  const t = useT();
  const view = useGame((g) => g.view);
  const map = useGame((g) => g.map);
  const grid = useGame((g) => g.grid);
  const log = useGame((g) => g.log);
  const [tab, setTab] = useState(1);
  const ranks = useMemo(() => (view && map && grid ? regionRanks(view, map, grid) : null), [view, map, grid]);
  if (!view || !map || !grid || !ranks) return null;
  const w = view.weather;
  const f = w.forecast;
  const shares = theatreShares(view, map);
  const regions = map.regions
    .map((r) => r.id)
    .sort(
      (a, b) =>
        Number(ranks.front.has(b)) - Number(ranks.front.has(a)) ||
        Number(ranks.ours.has(b)) - Number(ranks.ours.has(a)) ||
        regionName(map.regions[a].name, t.locale).localeCompare(
          regionName(map.regions[b].name, t.locale),
          t.locale,
        ),
    );
  const ctx = { view, map, grid, log };
  const met = log
    .filter((e) => e.kind === 'weatherForecast' || e.kind === 'weatherChange')
    .slice(-4)
    .reverse();
  const chips: [string, number, 'rain' | 'mud' | 'fog' | 'frost' | 'snow'][] = [
    [t('met.rain'), shares.rain, 'rain'],
    [t('met.mud'), shares.mud, 'mud'],
    [t('met.fog'), shares.fog, 'fog'],
    [t('met.snow'), shares.snow, 'snow'],
    [t('met.frozen'), shares.frozen, 'frost'],
  ];
  return (
    <>
      <DrawerHead
        title={t('met.title')}
        sub={t('met.sub', { clock: formatClock(f.issuedAt), day: dayOf(f.issuedAt) })}
      />
      <div className={hud.drawerBody}>
        <div className={m.now}>
          <span className={m.season} title={seasonBlurb(w.season, t.locale)}>
            {seasonLabel(w.season, t.locale)}
          </span>
          <span className={m.temp}>{formatTemp(w.temperature, t.locale)}</span>
          <span className={m.nowSub}>
            {t('met.now', {
              min: formatTemp(f.tempMin, t.locale),
              max: formatTemp(f.tempMax, t.locale),
              wind: t('wind.from', { dir: windFrom(w.wind) }),
            })}
          </span>
        </div>
        <div className={m.chips}>
          {chips.map(([label, share, icon]) => (
            <span
              key={label}
              className={cx(m.chip, share < 0.005 && m.chipOff)}
              title={t('met.chip.title', { label })}
            >
              <Icon name={icon} size={14} />
              {label} {t.pct(share).replace(/\s/g, '')}
            </span>
          ))}
        </div>

        <div className={d.section}>
          <div className={d.sectionTitle}>
            <span>{t('met.outlook')}</span>
            <span className={m.tabs}>
              {STEPS.map((s) => (
                <button
                  key={s}
                  type="button"
                  className={cx(m.tab, tab === s && m.tabOn)}
                  onClick={() => setTab(s)}
                >
                  {t('met.lead', { h: f.leads[s] })}
                </button>
              ))}
            </span>
          </div>
          <OutlookMap step={tab} />
          <div className={m.legend}>
            {(['rain', 'storm', 'snow'] as const).map((k) => (
              <span key={k} className={m.legendItem}>
                <span className={m.swatch} style={{ background: `rgb(${OUTLOOK_RGB[k].join(',')})` }} />
                {outlookWord(t, k)}
              </span>
            ))}
            <span className={m.legendNote}>
              {t('met.legendNote', { clock: formatClock(f.issuedAt + f.leads[tab]) })}
            </span>
          </div>
        </div>

        <div className={d.section}>
          <div className={cx(d.sectionTitle, m.tableHead)}>
            <span>{t('met.regions')}</span>
            {STEPS.map((s) => (
              <span key={s} className={m.col}>
                {formatClock(f.issuedAt + f.leads[s])}
              </span>
            ))}
            <span className={m.col} title={t('met.fogCol.hint')}>
              {t('met.fogCol')}
            </span>
          </div>
          {regions.map((r) => (
            <button
              key={r}
              type="button"
              className={cx(m.row, ranks.front.has(r) && m.rowFront)}
              onClick={() => bus.emit('focusHex', { hex: map.regions[r].hex })}
              title={ranks.front.has(r) ? t('met.front') : undefined}
            >
              <span className={m.name}>{regionName(map.regions[r].name, t.locale)}</span>
              {STEPS.map((s) => (
                <Cell key={s} o={outlookAt(f, r, s)} />
              ))}
              <span className={cx(m.cell, f.fog[r] >= 40 && m.o_fog)}>
                <span className={m.chance}>{f.fog[r] >= 10 ? `${f.fog[r]}%` : '—'}</span>
              </span>
            </button>
          ))}
        </div>

        <div className={d.section}>
          <div className={d.sectionTitle}>
            <span>{t('met.latest')}</span>
          </div>
          {met.length === 0 && <div className={d.muted}>{t('met.noWarnings')}</div>}
          {met.map((e) => {
            const line = describeEvent(e, ctx, t.locale);
            return (
              <button
                key={`${e.tick}:${e.kind}:${line.text}`}
                type="button"
                className={m.warn}
                onClick={() => line.hex >= 0 && bus.emit('focusHex', { hex: line.hex })}
              >
                <span className={m.warnHead}>
                  {line.head} <span className={m.warnAge}>{formatDate(e.tick, t.locale)}</span>
                </span>
                <span>{line.text}</span>
              </button>
            );
          })}
        </div>
      </div>
    </>
  );
}
